import React, { useState, useEffect } from 'react';
import { Project } from '../types';
import axios from 'axios';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { ChevronDown, RefreshCw, GitBranch, Folder, File, ChevronRight } from 'lucide-react';

interface AnalysisScreenProps {
  project: Project;
  onBack: () => void;
  username?: string;
  onLogout?: () => void;
  onNavigate?: (screen: string, testId?: string, projectId?: string) => void;
}

interface GitLabFile {
  file_name: string;
  file_path: string;
  content?: string;
  ref?: string;
  type: string;
}

interface Scenario {
  id: string;
  name: string;
  originalName: string;
  content?: string;
}

interface FileNode {
  name: string;
  path: string;
  type: string;
  children: { [key: string]: FileNode };
  isExpanded?: boolean;
}

interface RepoStatus {
  status: 'not_cloned' | 'cloned';
  branches: Array<{
    branch: string;
    hasChanges: boolean;
    lastCommit: string;
  }>;
  currentBranch: string;
  hasChanges: boolean;
  lastCommit: string;
}

interface GitLabData {
  files: GitLabFile[];
  selectedFile: GitLabFile | null;
  branches: string[];
  currentBranch: string;
  repoStatus: RepoStatus | null;
}

interface TestCoverage {
  totalLines: number;
  coveredLines: number;
  coveragePercentage: number;
  files: {
    path: string;
    coverage: number;
    coveredLines: number[];
    uncoveredLines: number[];
  }[];
}

interface CodeComplexity {
  files: {
    path: string;
    cyclomaticComplexity: number;
    cognitiveComplexity: number;
    linesOfCode: number;
  }[];
}

interface CodeQuality {
  files: {
    path: string;
    issues: {
      type: string;
      line: number;
      message: string;
    }[];
    issueCount: number;
  }[];
}

const API_URL = process.env.REACT_APP_API_HOST;

const AnalysisScreen: React.FC<AnalysisScreenProps> = ({ project, onBack, username, onLogout, onNavigate }) => {
  const [selectedAnalysis, setSelectedAnalysis] = useState<string | null>(null);
  const [gitlabData, setGitlabData] = useState<GitLabData>({
    files: [],
    selectedFile: null,
    branches: [],
    currentBranch: 'main',
    repoStatus: null
  });
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [testCoverage, setTestCoverage] = useState<TestCoverage | null>(null);
  const [codeComplexity, setCodeComplexity] = useState<CodeComplexity | null>(null);
  const [codeQuality, setCodeQuality] = useState<CodeQuality | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['']));

  useEffect(() => {
    const path = window.location.pathname;
    const parts = path.split('/');
    if (parts.length === 4 && parts[1] === 'analysis') {
      const analysisType = parts[3];
      setSelectedAnalysis(analysisType);
      if (analysisType === 'gitlab-code') {
        fetchGitlabData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to sync URL to state
  }, []);

  useEffect(() => {
    if (selectedAnalysis === 'gitlab-code') {
      fetchGitlabData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchGitlabData depends on project/gitlabData, stable enough
  }, [selectedAnalysis]);

  useEffect(() => {
    if (selectedAnalysis === 'scenarios') {
      fetchScenarios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchScenarios depends on project
  }, [selectedAnalysis]);

  useEffect(() => {
    if (selectedAnalysis === 'test-coverage') {
      analyzeTestCoverage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyzeTestCoverage depends on project
  }, [selectedAnalysis]);

  useEffect(() => {
    if (selectedAnalysis === 'code-complexity') {
      analyzeCodeComplexity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyzeCodeComplexity depends on project
  }, [selectedAnalysis]);

  useEffect(() => {
    if (selectedAnalysis === 'code-quality') {
      analyzeCodeQuality();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- analyzeCodeQuality depends on project
  }, [selectedAnalysis]);

  const handleAnalysisSelect = (analysisId: string) => {
    setSelectedAnalysis(analysisId);
    window.history.pushState({}, '', `/analysis/${project.id}/${analysisId}`);
  };

  const handleNavigate = (screen: string, testId?: string) => {
    if (testId) {
      window.history.pushState({}, '', `/test/${project.id}/${testId}`);
      onNavigate?.(screen, testId, project.id);
    } else {
      window.history.pushState({}, '', '/home');
      onNavigate?.('home');
    }
  };

  const handleLastTestClick = () => {
    const lastTestId = localStorage.getItem('lastTestId');
    if (lastTestId) {
      handleNavigate('test', lastTestId);
    }
  };
  // Passed to AnalysisContainer or used when adding "last test" link in analysis view
  void handleLastTestClick;

  const analysisOptions = [
    {
      id: 'test-coverage',
      name: 'Pokrycie testami',
      description: 'Analiza pokrycia testami dla wybranego projektu',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'code-complexity',
      name: 'Złożoność kodu',
      description: 'Analiza złożoności cyklomatycznej i kognitywnej',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'code-quality',
      name: 'Jakość kodu',
      description: 'Analiza jakości i standardów kodowania',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      id: 'test-results',
      name: 'Wyniki testów',
      description: 'Statystyki i analiza wyników testów',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'scenarios',
      name: 'Scenariusze testowe',
      description: 'Podgląd i analiza scenariuszy testowych',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    },
    {
      id: 'gitlab-code',
      name: 'Kod źródłowy',
      description: 'Podgląd kodu źródłowego z GitLab',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    }
  ];

  const fetchGitlabData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (!project.gitlab?.defaultProject) {
        setError('Brak skonfigurowanego projektu GitLab. Skonfiguruj integrację w ustawieniach.');
        return;
      }

      // Najpierw sprawdź status repozytorium
      const statusResponse = await axios.get<RepoStatus>(`${API_URL}/api/gitlab/${project.id}/repo-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setGitlabData(prev => ({
        ...prev,
        repoStatus: statusResponse.data
      }));

      // Jeśli repozytorium nie jest sklonowane, sklonuj je
      if (statusResponse.data.status === 'not_cloned') {
        await axios.post(`${API_URL}/api/gitlab/${project.id}/clone`, null, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      // Pobierz listę gałęzi z GitLab API
      const branchesResponse = await axios.get<string[]>(`${API_URL}/api/gitlab/${project.id}/branches`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Jeśli nie ma wybranej gałęzi lub wybrana gałąź nie istnieje, użyj pierwszej dostępnej
      const availableBranches = branchesResponse.data;
      const currentBranch = availableBranches.includes(gitlabData.currentBranch) 
        ? gitlabData.currentBranch 
        : availableBranches[0] || 'main';

      // Pobierz listę plików z lokalnego repozytorium dla aktualnej gałęzi
      const filesResponse = await axios.get<GitLabFile[]>(`${API_URL}/api/gitlab/${project.id}/repo-files`, {
        params: {
          branch: currentBranch
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setGitlabData(prev => ({
        ...prev,
        files: filesResponse.data,
        branches: availableBranches,
        currentBranch: currentBranch
      }));
    } catch (error: any) {
      setError('Nie udało się pobrać danych. Sprawdź konfigurację i spróbuj ponownie.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (file: GitLabFile) => {
    try {
      const response = await axios.get<GitLabFile>(
        `${API_URL}/api/gitlab/${project.id}/repo-files/content/${encodeURIComponent(file.file_path)}`,
        {
          params: {
            branch: gitlabData.currentBranch
          },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setGitlabData(prev => ({
        ...prev,
        selectedFile: response.data
      }));
    } catch {
      setGitlabData(prev => ({
        ...prev,
        selectedFile: null
      }));
    }
  };

  const handleBranchChange = async (branch: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Update the current branch in state
      setGitlabData(prev => ({
        ...prev,
        currentBranch: branch,
        selectedFile: null
      }));

      // Fetch files for the new branch
      const filesResponse = await axios.get<GitLabFile[]>(`${API_URL}/api/gitlab/${project.id}/repo-files`, {
        params: {
          branch: branch
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Update the files in state
      setGitlabData(prev => ({
        ...prev,
        files: filesResponse.data
      }));
    } catch {
      setError('Nie udało się zmienić gałęzi. Spróbuj ponownie.');
    } finally {
      setIsLoading(false);
    }
  };

  const sortFilesByTypeAndName = (a: GitLabFile, b: GitLabFile) => {
    if (a.type !== b.type) {
      return a.type === 'tree' ? -1 : 1;
    }
    return a.file_name.localeCompare(b.file_name);
  };

  const buildFileTree = (files: GitLabFile[]): FileNode => {
    const root: FileNode = {
      name: '',
      path: '',
      type: 'tree',
      children: {},
      isExpanded: false
    };
    
    [...files].sort(sortFilesByTypeAndName).forEach(file => {
      const parts = file.file_path.split('/');
      let currentLevel = root.children;
      
      parts.forEach((part, index) => {
        if (!currentLevel[part]) {
          currentLevel[part] = {
            name: part,
            path: parts.slice(0, index + 1).join('/'),
            type: index === parts.length - 1 ? file.type : 'tree',
            children: {},
            isExpanded: false
          };
        }
        if (index < parts.length - 1) {
          currentLevel = currentLevel[part].children;
        }
      });
    });
    
    return root;
  };

  const toggleFolder = (path: string) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const renderFileTree = (node: FileNode, level: number = 0) => {
    const children = Object.values(node.children).sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'tree' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });

    const isExpanded = expandedFolders.has(node.path);
    const isActive = gitlabData.selectedFile?.file_path === node.path;

    return (
      <div key={node.path} style={{ marginLeft: `${level * 20}px` }}>
        {node.path !== '' && (
          <div
            onClick={() => {
              if (node.type === 'tree') {
                toggleFolder(node.path);
              } else {
                handleFileSelect({
                  file_name: node.name,
                  file_path: node.path,
                  type: 'blob'
                });
              }
            }}
            className={`
              group flex items-center gap-2 px-3 py-2 rounded-md transition-all duration-200
              ${node.type === 'blob' 
                ? 'cursor-pointer hover:bg-slate-100' 
                : 'cursor-pointer'
              }
              ${isActive 
                ? 'bg-blue-50 border-l-4 border-blue-600 text-blue-600 font-medium' 
                : 'hover:bg-slate-50'
              }
              ${node.type === 'tree' ? 'bg-slate-50/50' : ''}
            `}
          >
            {node.type === 'tree' ? (
              <div className="flex items-center gap-2">
                <ChevronRight 
                  className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} group-hover:text-slate-700`} 
                />
                <Folder 
                  className={`w-5 h-5 text-slate-500 group-hover:text-slate-700 ${isExpanded ? 'text-blue-500' : ''}`} 
                />
              </div>
            ) : (
              <File 
                className={`w-5 h-5 text-slate-500 group-hover:text-slate-700 ${isActive ? 'text-blue-500' : ''}`} 
              />
            )}
            <span className={`text-sm truncate ${isActive ? 'font-medium' : ''}`}>{node.name}</span>
          </div>
        )}
        {isExpanded && (
          <div className="mt-1">
            {children.map(child => renderFileTree(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const getLanguageFromFileName = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'js':
      case 'jsx':
        return 'javascript';
      case 'ts':
      case 'tsx':
        return 'typescript';
      case 'json':
        return 'json';
      case 'html':
        return 'html';
      case 'css':
        return 'css';
      case 'md':
        return 'markdown';
      case 'py':
        return 'python';
      case 'java':
        return 'java';
      case 'php':
        return 'php';
      case 'rb':
        return 'ruby';
      case 'go':
        return 'go';
      case 'rs':
        return 'rust';
      case 'sh':
        return 'bash';
      case 'yml':
      case 'yaml':
        return 'yaml';
      case 'xml':
        return 'xml';
      default:
        return 'plaintext';
    }
  };

  const renderGitlabCodeView = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Błąd</h3>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={fetchGitlabData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Spróbuj ponownie
          </button>
        </div>
      );
    }

    if (!gitlabData.files.length) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Integracja z GitLab</h3>
          <p className="text-slate-600">
            Aby włączyć podgląd kodu źródłowego, należy skonfigurować integrację z GitLab.
          </p>
          <button
            onClick={() => window.open('https://gitlab.com', '_blank')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Konfiguruj GitLab
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </h1>
              {gitlabData.repoStatus && gitlabData.repoStatus.status === 'cloned' && (
                <div className="text-sm text-slate-600 mt-3">
                  <div className="flex flex-wrap gap-2">
                    {gitlabData.repoStatus.branches && gitlabData.repoStatus.branches.map((branchStatus) => (
                      <div key={branchStatus.branch} className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          branchStatus.branch === gitlabData.currentBranch
                            ? 'bg-blue-100 text-blue-800 border border-blue-200'
                            : 'bg-slate-100 text-slate-800 border border-slate-200'
                        }`}>
                          {branchStatus.branch}
                        </span>
                      </div>
                    ))}
                  </div>
                  {gitlabData.repoStatus.branches && gitlabData.repoStatus.branches.some(b => b.hasChanges) && (
                    <div className="text-xs mt-3">
                      <span className="inline-flex items-center px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium border border-yellow-200">
                        Zmiany na: {gitlabData.repoStatus.branches.filter(b => b.hasChanges).map(b => b.branch).join(', ')}
                      </span>
                    </div>
                  )}
                  {gitlabData.repoStatus.branches && (
                    <div className="text-xs mt-3 text-slate-500">
                      Ostatni commit: {gitlabData.repoStatus.branches.find(b => b.branch === gitlabData.currentBranch)?.lastCommit || 'Brak'}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <select 
                  className="appearance-none border border-slate-200 rounded-md px-10 py-2 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700"
                  value={gitlabData.currentBranch}
                  onChange={(e) => handleBranchChange(e.target.value)}
                >
                  {gitlabData.branches && gitlabData.branches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-slate-500" />
                </div>
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <GitBranch className="w-4 h-4 text-slate-500" />
                </div>
              </div>
              <button 
                onClick={fetchGitlabData}
                className="inline-flex items-center px-4 py-2 bg-white text-slate-700 rounded-md hover:bg-slate-50 transition-colors shadow-sm border border-slate-200 text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4 mr-2 text-slate-500" />
                Odśwież
              </button>
              <button 
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    await axios.post(`${API_URL}/api/gitlab/${project.id}/sync`, null, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    });
                    await fetchGitlabData();
                  } catch {
                    setError('Nie udało się zsynchronizować zmian. Spróbuj ponownie.');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Synchronizuj
              </button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-12 gap-6">
          {/* File Tree */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                Pliki
              </h2>
              <div className="space-y-0.5 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {gitlabData.files.length > 0 && renderFileTree(buildFileTree(gitlabData.files))}
              </div>
            </div>
          </div>

          {/* File Content */}
          <div className="col-span-9">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100 min-h-[600px]">
              {gitlabData.selectedFile ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {gitlabData.selectedFile.file_name}
                    </h2>
                    <span className="text-sm text-slate-500 bg-slate-50 px-3 py-1 rounded-full border border-slate-200">
                      Gałąź: {gitlabData.currentBranch}
                    </span>
                  </div>
                  <div className="relative rounded-lg overflow-hidden border border-slate-200">
                    <div className="absolute top-0 right-0 px-3 py-1 bg-slate-100 text-slate-500 text-xs border-b border-l border-slate-200 rounded-bl">
                      {getLanguageFromFileName(gitlabData.selectedFile.file_name)}
                    </div>
                    <SyntaxHighlighter
                      language={getLanguageFromFileName(gitlabData.selectedFile.file_name)}
                      style={vscDarkPlus}
                      customStyle={{
                        margin: 0,
                        padding: '2.5rem 1rem 1rem',
                        background: '#1e1e1e',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                      }}
                      showLineNumbers
                      wrapLines
                      lineNumberStyle={{
                        color: '#6b7280',
                        marginRight: '1rem',
                        userSelect: 'none',
                      }}
                    >
                      {typeof gitlabData.selectedFile.content === 'object' 
                        ? JSON.stringify(gitlabData.selectedFile.content, null, 2)
                        : gitlabData.selectedFile.content || ''}
                    </SyntaxHighlighter>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">Wybierz plik</h3>
                  <p className="text-slate-500 max-w-md">
                    Wybierz plik z drzewa po lewej stronie, aby zobaczyć jego zawartość
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const fetchScenarios = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use local CSV files data from the project object
      const scenariosData = project.csvFiles.map(file => ({
        id: file.id,
        name: file.name,
        originalName: file.originalName
      }));
      setScenarios(scenariosData);
    } catch {
      setError('Nie udało się przetworzyć scenariuszy. Spróbuj ponownie.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleScenarioSelect = async (scenario: Scenario) => {
    try {
      // For now, we'll just set the selected scenario without content
      // In a real implementation, you would load the CSV file content here
      setSelectedScenario(scenario);
    } catch {
      setError('Nie udało się wybrać scenariusza. Spróbuj ponownie.');
    }
  };

  const renderScenariosView = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Błąd</h3>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={fetchScenarios}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Spróbuj ponownie
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Scenariusze testowe
              </h1>
              <p className="text-slate-600 mt-2">
                Liczba scenariuszy: {scenarios.length}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={fetchScenarios}
                className="inline-flex items-center px-4 py-2 bg-white text-slate-700 rounded-md hover:bg-slate-50 transition-colors shadow-sm border border-slate-200 text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4 mr-2 text-slate-500" />
                Odśwież
              </button>
              <button 
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    setError(null);
                    await axios.post(`${API_URL}/api/gitlab/${project.id}/export-scenarios`, {
                      scenarios: scenarios
                    }, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    });
                    alert('Scenariusze zostały wyeksportowane do gałęzi test-scenarios w repozytorium GitLab');
                  } catch {
                    setError('Nie udało się wyeksportować scenariuszy. Spróbuj ponownie.');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Eksportuj do GitLab
              </button>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-12 gap-6">
          {/* Scenarios List */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Lista scenariuszy
              </h2>
              <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {scenarios.map((scenario) => (
                  <div
                    key={scenario.id}
                    onClick={() => handleScenarioSelect(scenario)}
                    className={`
                      p-3 rounded-lg cursor-pointer transition-all duration-200
                      ${selectedScenario?.id === scenario.id 
                        ? 'bg-blue-50 border-l-4 border-blue-600 text-blue-600 font-medium' 
                        : 'hover:bg-slate-50 border-l-4 border-transparent'
                      }
                    `}
                  >
                    <div className="font-medium">{scenario.name}</div>
                    <div className="text-sm text-slate-500">{scenario.originalName}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Scenario Content */}
          <div className="col-span-9">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100 min-h-[600px]">
              {selectedScenario ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                      <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {selectedScenario.name}
                    </h2>
                  </div>
                  <div className="relative rounded-lg overflow-hidden border border-slate-200">
                    <div className="absolute top-0 right-0 px-3 py-1 bg-slate-100 text-slate-500 text-xs border-b border-l border-slate-200 rounded-bl">
                      CSV
                    </div>
                    <SyntaxHighlighter
                      language="csv"
                      style={vscDarkPlus}
                      customStyle={{
                        margin: 0,
                        padding: '2.5rem 1rem 1rem',
                        background: '#1e1e1e',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                      }}
                      showLineNumbers
                      wrapLines
                      lineNumberStyle={{
                        color: '#6b7280',
                        marginRight: '1rem',
                        userSelect: 'none',
                      }}
                    >
                      {selectedScenario.content || ''}
                    </SyntaxHighlighter>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                  <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="text-lg font-medium text-slate-800 mb-2">Wybierz scenariusz</h3>
                  <p className="text-slate-500 max-w-md">
                    Wybierz scenariusz z listy po lewej stronie, aby zobaczyć jego zawartość
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const analyzeTestCoverage = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post<TestCoverage>(
        `${API_URL}/api/analyze/${project.id}/test-coverage`,
        null,
        {
          params: {
            branch: gitlabData.currentBranch
          },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setTestCoverage(response.data);
    } catch {
      setError('Nie udało się przeanalizować pokrycia testami. Spróbuj ponownie.');
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeCodeComplexity = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post<CodeComplexity>(
        `${API_URL}/api/analyze/${project.id}/complexity`,
        null,
        {
          params: {
            branch: gitlabData.currentBranch
          },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setCodeComplexity(response.data);
    } catch {
      setError('Nie udało się przeanalizować złożoności kodu. Spróbuj ponownie.');
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeCodeQuality = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post<CodeQuality>(
        `${API_URL}/api/analyze/${project.id}/quality`,
        null,
        {
          params: {
            branch: gitlabData.currentBranch
          },
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      setCodeQuality(response.data);
    } catch {
      setError('Nie udało się przeanalizować jakości kodu. Spróbuj ponownie.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderTestCoverageView = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Błąd</h3>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={analyzeTestCoverage}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Spróbuj ponownie
          </button>
        </div>
      );
    }

    if (!testCoverage) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Analiza pokrycia testami</h3>
          <p className="text-slate-600 mb-4">
            Kliknij przycisk poniżej, aby rozpocząć analizę pokrycia kodu scenariuszami testowymi.
          </p>
          <button
            onClick={analyzeTestCoverage}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Rozpocznij analizę
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Coverage Summary */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Podsumowanie pokrycia testami
              </h1>
              <div className="mt-4 grid grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="text-sm text-slate-500">Całkowita liczba linii</div>
                  <div className="text-2xl font-semibold text-slate-800">{testCoverage.totalLines || 0}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="text-sm text-slate-500">Pokryte linie</div>
                  <div className="text-2xl font-semibold text-slate-800">{testCoverage.coveredLines || 0}</div>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="text-sm text-slate-500">Procent pokrycia</div>
                  <div className="text-2xl font-semibold text-slate-800">
                    {testCoverage.coveragePercentage ? testCoverage.coveragePercentage.toFixed(2) : '0.00'}%
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={analyzeTestCoverage}
                className="inline-flex items-center px-4 py-2 bg-white text-slate-700 rounded-md hover:bg-slate-50 transition-colors shadow-sm border border-slate-200 text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4 mr-2 text-slate-500" />
                Odśwież
              </button>
            </div>
          </div>
        </div>

        {/* Files Coverage */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Pokrycie plików</h2>
          <div className="space-y-4">
            {testCoverage.files && testCoverage.files.map((file) => (
              <div key={file.path} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-slate-800">{file.path}</div>
                  <div className="text-sm font-medium px-3 py-1 rounded-full bg-slate-100 text-slate-700">
                    {file.coverage ? file.coverage.toFixed(2) : '0.00'}%
                  </div>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${file.coverage || 0}%` }}
                  />
                </div>
                <div className="mt-2 text-sm text-slate-500">
                  Pokryte linie: {file.coveredLines?.length || 0}, Niepokryte linie: {file.uncoveredLines?.length || 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCodeComplexityView = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Błąd</h3>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={analyzeCodeComplexity}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Spróbuj ponownie
          </button>
        </div>
      );
    }

    if (!codeComplexity) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Analiza złożoności kodu</h3>
          <p className="text-slate-600 mb-4">
            Kliknij przycisk poniżej, aby rozpocząć analizę złożoności kodu.
          </p>
          <button
            onClick={analyzeCodeComplexity}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Rozpocznij analizę
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Złożoność plików</h2>
          <div className="space-y-4">
            {codeComplexity.files.map((file) => (
              <div key={file.path} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-slate-800">{file.path}</div>
                  <div className="text-sm font-medium px-3 py-1 rounded-full bg-slate-100 text-slate-700">
                    {file.linesOfCode} linii
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <div className="text-sm text-slate-500">Złożoność cyklomatyczna</div>
                    <div className="text-xl font-semibold text-slate-800">{file.cyclomaticComplexity}</div>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <div className="text-sm text-slate-500">Złożoność kognitywna</div>
                    <div className="text-xl font-semibold text-slate-800">{file.cognitiveComplexity}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCodeQualityView = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Błąd</h3>
          <p className="text-slate-600 mb-4">{error}</p>
          <button
            onClick={analyzeCodeQuality}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Spróbuj ponownie
          </button>
        </div>
      );
    }

    if (!codeQuality) {
      return (
        <div className="text-center p-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Analiza jakości kodu</h3>
          <p className="text-slate-600 mb-4">
            Kliknij przycisk poniżej, aby rozpocząć analizę jakości kodu.
          </p>
          <button
            onClick={analyzeCodeQuality}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            Rozpocznij analizę
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-100">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Jakość kodu</h2>
          <div className="space-y-4">
            {codeQuality.files.map((file) => (
              <div key={file.path} className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-slate-800">{file.path}</div>
                  <div className={`text-sm font-medium px-3 py-1 rounded-full ${
                    file.issueCount === 0 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {file.issueCount} problemów
                  </div>
                </div>
                {file.issues.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {file.issues.map((issue, index) => (
                      <div key={index} className="flex items-start gap-2 text-sm">
                        <div className="mt-1">
                          <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">Linia {issue.line}: {issue.message}</div>
                          <div className="text-slate-500">{issue.type}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-12 pt-24">
      <div className="w-full max-w-[2000px] mx-auto bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <button
              onClick={onBack}
              className="mr-4 p-2 rounded-lg bg-white shadow-lg hover:bg-gray-100 transition-colors"
            >
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-4xl font-bold text-slate-800 tracking-tight">Analiza projektu</h1>
              <p className="text-slate-600 mt-1 text-lg">{project.name}</p>
            </div>
          </div>
        </div>

        {/* Project Info Banner */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 transform transition-all duration-200 hover:shadow-2xl">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold text-slate-800 flex items-center gap-3">
                <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Informacje o projekcie
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <div>
                <div className="text-sm text-slate-500">Nazwa projektu</div>
                <div className="font-medium text-slate-800">{project.name}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="text-sm text-slate-500">Data utworzenia</div>
                <div className="font-medium text-slate-800">
                  {new Date(project.createdAt).toLocaleString('pl-PL', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {analysisOptions.map((option) => (
            <div
              key={option.id}
              onClick={() => handleAnalysisSelect(option.id)}
              className={`p-6 bg-white rounded-xl shadow-md cursor-pointer transition-all duration-200 hover:shadow-lg border-2 transform hover:-translate-y-1 ${
                selectedAnalysis === option.id 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-transparent hover:border-slate-200'
              }`}
            >
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg ${
                  selectedAnalysis === option.id 
                    ? 'bg-blue-100 text-blue-600' 
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {option.icon}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">{option.name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Analysis Content */}
        {selectedAnalysis && (
          <div className="mt-8 bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-200 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-slate-800 flex items-center gap-3">
                <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {analysisOptions.find(opt => opt.id === selectedAnalysis)?.name}
              </h2>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              {selectedAnalysis === 'gitlab-code' ? (
                renderGitlabCodeView()
              ) : selectedAnalysis === 'scenarios' ? (
                renderScenariosView()
              ) : selectedAnalysis === 'test-coverage' ? (
                renderTestCoverageView()
              ) : selectedAnalysis === 'code-complexity' ? (
                renderCodeComplexityView()
              ) : selectedAnalysis === 'code-quality' ? (
                renderCodeQualityView()
              ) : (
                <div className="text-slate-600">
                  <p>Analiza w trakcie implementacji...</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisScreen; 